import type { CustomClickedElementsResponse, IFetchHeatmapDataProps } from './customs';
import type { IClickMapMetric } from '@gem-sdk/heatmap-react';
import type { IClickedElement } from '../types';

import {
  getTotalClickedMetric,
  getRevenuePerClickMetric,
  getRevenuePerSessionMetric,
  getTotalOrderMetric,
  getTotalRevenueMetric,
  getTotalSessionMetric,
  getClickConversionRateMetric,
} from '../helpers/metric';
import { useHeatmapDataContext, useHeatmapSettingContext } from '@gem-sdk/heatmap-react';
import { MOCK_CLICKED_ELEMENTS, useMockLoading } from './mocks';
import { EHeatmapLoadingKey, EHeatmapType } from '../types';
import { useFeatureFlagChecker } from '~/modules/apps';
import { useCustomClickedElementsQuery } from '../api';
import { useHeatmapBaseCachedQuery } from './customs';
import { useHeatmapLoadingContext } from '../hooks';
import { omitWhereInput } from '../helpers';
import { useEffect, useMemo } from 'react';

export enum ESelectorToken {
  TEXT = 0,
  ID = 1,
  CLASS = 2,
  TAG = 3,
}

export const splitHash = (hash: string): { hashAlpha: string; hashBeta: string } => {
  const hashParts = hash.split('.');
  const hashAlpha = hashParts.at(0) ?? '';
  const hashBeta = hashParts.at(1) ?? '';
  return { hashAlpha, hashBeta };
};

const buildSelector = (selector: IClickedElement['selector']): string => {
  if (!selector) return '';

  const text = selector[ESelectorToken.TEXT];
  if (text) return text;

  const id = selector[ESelectorToken.ID];
  if (id) return `#${id}`;

  const classes = selector[ESelectorToken.CLASS];
  if (classes) return `.${classes.split(' ').join('.')}`;

  const tag = selector[ESelectorToken.TAG];
  if (tag) return tag;

  return '';
};

const convertFn = (data?: HeatmapClickedElements): IClickMapMetric => {
  if (!data) return {};

  const acc: IClickMapMetric = {};

  for (const item of data) {
    const metric = {
      totalClicked: item.totalClicks,
      totalOrders: item.totalOrders,
      totalRevenue: item.totalRevenue,
      totalSessions: item.totalSessions,
      totalSessionsThatCompletedCheckout: item.totalSessionsThatCompletedCheckout,
      totalClicksWithRevenue: item.totalClicksWithRevenue,
    };

    const { hashBeta, hashAlpha } = splitHash(item.hash);
    if (!hashBeta) continue;

    acc[hashBeta] = {
      hash: hashBeta,
      hashAlpha: hashAlpha ?? '',
      hashPaths: item.hash,
      selector: buildSelector(item.selector),
      listSelectors: item.selector?.map((i) => i ?? '') ?? [],
      totalClicked: getTotalClickedMetric({ metric }),
      totalOrders: getTotalOrderMetric({ metric }),
      totalRevenue: getTotalRevenueMetric({ metric }),
      totalSessions: getTotalSessionMetric({ metric }),
      conversionRate: getClickConversionRateMetric({ metric }),
      revenuePerClick: getRevenuePerClickMetric({ metric }),
      revenuePerSession: getRevenuePerSessionMetric({ metric }),
    };
  }

  return acc;
};

type HeatmapClickedElements = CustomClickedElementsResponse['clickedElements'];
type HeatmapClickedElementsResponse = CustomClickedElementsResponse;

interface IFetchClickedElementsResult {
  data?: HeatmapClickedElements;
  isLoading: boolean;
}
export const useFetchClickedElements = (
  props: IFetchHeatmapDataProps,
): IFetchClickedElementsResult => {
  const heatmapType = useHeatmapSettingContext((s) => s.heatmapType);
  const setDataInfoByKey = useHeatmapDataContext((s) => s.setDataInfoByKey);
  const setLoadingByKey = useHeatmapLoadingContext((s) => s.setLoadingByKey);

  const { isEnabledHeatmapMockData } = useFeatureFlagChecker();

  // TODO: Remove deviceTypes from whereInput once we have a proper device type filter
  const whereInput = useMemo(
    () => omitWhereInput(props.whereInput, ['scrollType']),
    [props.whereInput],
  );

  const { data: rawData, isLoading: isLoadingRaw } = useHeatmapBaseCachedQuery<
    HeatmapClickedElements,
    HeatmapClickedElementsResponse
  >(
    {
      ...props,
      whereInput,
      enabled: props.enabled && heatmapType === EHeatmapType.Click,
    },
    {
      cacheSelector: (state) => state.clickedElementsCache,
      upsertSelector: (state) => state.upsertClickedElementsCache,
      getResponseData: (response) => response.clickedElements,
      useQuery: useCustomClickedElementsQuery,
    },
  );

  const { isMockLoading, dataMock } = useMockLoading(props.whereInput, MOCK_CLICKED_ELEMENTS);

  const data = isEnabledHeatmapMockData ? dataMock : rawData;
  const isLoading = isEnabledHeatmapMockData ? isMockLoading : isLoadingRaw;

  useEffect(() => {
    if (!data) return;

    setDataInfoByKey('clickMapMetrics', convertFn(data));
  }, [data, setDataInfoByKey]);

  useEffect(() => {
    setLoadingByKey(EHeatmapLoadingKey.CLICK_ELEMENT, isLoading);
  }, [isLoading, setLoadingByKey]);

  return { data, isLoading };
};
